import { useEffect, useRef, useState, type ReactNode, type SyntheticEvent } from "react";
import { api } from "../lib/api";
import { useRealtime } from "../realtime/RealtimeContext";
import { exerciseRunEndedEvent, exerciseRunStartedEvent } from "./learning-api";

type RunStatus = { status: string; abortReason: "INACTIVITY" | "TIMED_NO_ANSWER" | null };

export function ExerciseActivityBoundary({ pageId, children }: { pageId: string; children: ReactNode }) {
  const realtime = useRealtime();
  const [runId, setRunId] = useState("");
  const [abortReason, setAbortReason] = useState<RunStatus["abortReason"]>(null);
  const runIdRef = useRef("");
  const endedRunId = useRef("");
  const lastSentAt = useRef(0);
  const pending = useRef(false);
  const trailing = useRef<ReturnType<typeof setTimeout> | null>(null);

  function activate(id: string) {
    if (endedRunId.current === id) return;
    if (runIdRef.current === id) return;
    runIdRef.current = id;
    lastSentAt.current = 0;
    pending.current = false;
    setAbortReason(null);
    setRunId(id);
  }

  function clear(id: string) {
    if (runIdRef.current !== id) return;
    endedRunId.current = id;
    runIdRef.current = "";
    pending.current = false;
    if (trailing.current) clearTimeout(trailing.current);
    trailing.current = null;
    setRunId("");
  }

  function acceptStatus(id: string, result: RunStatus) {
    if (runIdRef.current !== id || result.status === "STARTED") return;
    if (result.status === "ABORTED" && result.abortReason) setAbortReason(result.abortReason);
    clear(id);
  }

  async function sendActivity(keepalive = false) {
    const id = runIdRef.current;
    if (!id || !pending.current) return;
    pending.current = false;
    lastSentAt.current = Date.now();
    try {
      acceptStatus(id, await api<RunStatus>(`/learning/runs/${encodeURIComponent(id)}/activity`, { method: "POST", keepalive }));
    } catch {
      // The server deadline remains authoritative if a tab goes offline.
    }
  }

  function record(event: SyntheticEvent) {
    if (!event.nativeEvent.isTrusted || document.visibilityState !== "visible" || !runIdRef.current || abortReason) return;
    pending.current = true;
    if (Date.now() - lastSentAt.current >= 30_000) void sendActivity();
    if (trailing.current) clearTimeout(trailing.current);
    trailing.current = setTimeout(() => { void sendActivity(); }, 5_000);
  }

  useEffect(() => {
    const started = (event: Event) => {
      const id = (event as CustomEvent<{ runId?: string }>).detail?.runId;
      if (id) activate(id);
    };
    const ended = (event: Event) => {
      const detail = (event as CustomEvent<{ runId?: string; abortReason?: RunStatus["abortReason"] }>).detail;
      if (detail?.runId) {
        if (detail.abortReason) setAbortReason(detail.abortReason);
        clear(detail.runId);
      }
    };
    window.addEventListener(exerciseRunStartedEvent, started);
    window.addEventListener(exerciseRunEndedEvent, ended);
    return () => {
      window.removeEventListener(exerciseRunStartedEvent, started);
      window.removeEventListener(exerciseRunEndedEvent, ended);
      if (trailing.current) clearTimeout(trailing.current);
    };
  }, []);

  useEffect(() => {
    if (!realtime.session) return;
    const envelope = realtime.state.exercise as { pageId?: string; runId?: string; state?: { runId?: string; phase?: string; submitted?: boolean } | null } | null;
    if (envelope?.pageId !== pageId || !envelope.state || envelope.state.phase === "complete" || envelope.state.submitted) return;
    const id = envelope.runId ?? envelope.state.runId;
    if (id) activate(id);
  }, [realtime.session?.id, realtime.state.exercise, pageId]);

  useEffect(() => {
    if (!runId) return;
    const check = () => {
      if (document.visibilityState !== "visible") {
        if (pending.current) void sendActivity(true);
        return;
      }
      api<RunStatus>(`/learning/runs/${encodeURIComponent(runId)}/status`).then((result) => acceptStatus(runId, result)).catch(() => undefined);
    };
    document.addEventListener("visibilitychange", check);
    window.addEventListener("focus", check);
    check();
    return () => {
      document.removeEventListener("visibilitychange", check);
      window.removeEventListener("focus", check);
    };
  }, [runId]);

  function restart() {
    if (realtime.session) {
      const envelope = realtime.state.exercise as Record<string, unknown> | null;
      if (envelope?.pageId === pageId) realtime.publishExercise({ ...envelope, runId: "", state: null });
    }
    setAbortReason(null);
  }

  if (abortReason) return <section className="alert info" role="status">
    <strong>{abortReason === "INACTIVITY" ? "Dieser Durchgang wurde nach 15 Minuten ohne Eingabe abgebrochen." : "Dieser Zeitdurchgang endete ohne gespeicherte Antwort."}</strong>
    <p>{realtime.session?.participantRole === "PARTICIPANT" ? "Die Sitzungsleitung kann einen neuen Durchgang starten." : "Starte die Übung neu, um einen neuen Durchgang zu beginnen."}</p>
    {realtime.session?.participantRole !== "PARTICIPANT" && <button className="primary" type="button" onClick={restart}>Übung neu starten</button>}
  </section>;

  return <div style={{ display: "contents" }} onPointerDownCapture={record} onKeyDownCapture={record} onInputCapture={record}>{children}</div>;
}
